import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { CommonModule, CurrencyPipe, DatePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { Subject, takeUntil, tap, catchError, of } from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatTooltip } from '@angular/material/tooltip';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { QuoteRequestCartItem, ShoppingCartMessageType } from '../../shopping-cart.types';
import {
  OpenQuoteRequestsItem,
  Address,
  Quote,
  Shipping,
  OpenQuoteRequestsResponse,
} from '@sbd-account-portal/app/modules/dashboard/open-quote-requests-widget/quote-requests.types';
import { OpenQuoteRequestsWidgetService } from '@sbd-account-portal/app/modules/dashboard/open-quote-requests-widget/open-quote-requests-widget.service';
import { CathyService } from '@sbd-doug-frontend/app/services/cathy.service';
import { ActiveCommunicationContact } from '@sbd-doug-frontend/shared/lib/types/activecommunicationcontact.types';
import { activeCommunicationContactsFrom, jumpUrlWithRh } from '@sbd-doug-frontend/shared/lib/types/active-communication-contacts';
import { UserService } from '@sbd-account-portal/app/core/user/user.service';
import { SendReminderDialogComponent } from '@sbd-account-portal/app/modules/dashboard/open-quote-requests-widget/drawer-content/send-reminder-dialog.component';
import { EllipsisPipe } from '@sbd-doug-frontend/shared/lib/public-pipes';
import { isServiceEmail } from '@sbd-doug-frontend/shared/lib/public-validators';

interface CartMenuItem {
  label: string;
  icon?: string;
  enabled: boolean;
  action?: () => void;
}

type MessageType = 'send-message' | 'make-a-change';

@Component({
  selector: 'cipx-quote-request-cart-item',
  templateUrl: './quote-request-cart-item.component.html',
  styleUrl: './quote-request-cart-item.component.scss',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAccordion,
    MatExpansionPanelHeader,
    MatExpansionPanel,
    CurrencyPipe,
    DatePipe,
    NgClass,
    NgForOf,
    NgIf,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatIconButton,
    MatButton,
    MatDivider,
    MatCard,
    MatCardContent,
    MatTooltip,
    MatRadioButton,
    MatRadioGroup,
    MatFormFieldModule,
    MatSelectModule,
    EllipsisPipe,
  ],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
})
export class CIPXQuoteRequestCartItemComponent implements OnInit, OnDestroy {
  @Input() item!: QuoteRequestCartItem<OpenQuoteRequestsItem>;
  @Input() toggleAccordion: boolean = false;
  @Input() useAccordionComponent: boolean = true;
  @Input() fullDisplayWithoutAccordion: boolean = false;

  @Output() removeItem = new EventEmitter<QuoteRequestCartItem<OpenQuoteRequestsItem>>();
  @Output() updateQuantity = new EventEmitter<{ item: QuoteRequestCartItem<OpenQuoteRequestsItem>, quantity: number }>();
  @Output() declineQuote = new EventEmitter<QuoteRequestCartItem<OpenQuoteRequestsItem>>();
  @Output() viewDetails = new EventEmitter<QuoteRequestCartItem<OpenQuoteRequestsItem>>();
  @Output() updatedOpenQR = new EventEmitter<OpenQuoteRequestsItem>();
  @Output() itemUpdated = new EventEmitter<QuoteRequestCartItem<OpenQuoteRequestsItem>>();
  @Output() sendMessageFromDrawer: EventEmitter<{ item: QuoteRequestCartItem<OpenQuoteRequestsItem>, messageType: ShoppingCartMessageType }> =
    new EventEmitter<{ item: QuoteRequestCartItem<OpenQuoteRequestsItem>, messageType: ShoppingCartMessageType }>();

  readonly panelOpenState = signal(false);
  _fuseMediaWatcherService = inject(FuseMediaWatcherService);
  _changeDetector = inject(ChangeDetectorRef);
  _openQuoteRequestsWidgetService = inject(OpenQuoteRequestsWidgetService);
  _cathyService = inject(CathyService);
  _userService = inject(UserService);
  _dialog = inject(MatDialog);
  isScreenSmall: boolean = false;
  _unsubscribeAll: Subject<void> = new Subject<void>();
  menuItems: CartMenuItem[] = [];

  ngOnInit() {
    this.menuItems = this.createCartMenuItems();
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        this.isScreenSmall = !matchingAliases.includes('md');
      });
  }

  ngOnDestroy() {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onMenuItemClick(menuItem: CartMenuItem) {
    if (!menuItem.enabled) {
      return;
    }
    if (typeof menuItem.action === 'function') {
      menuItem.action();
    }
  }

  getDurationTerm(data: OpenQuoteRequestsItem | Quote) {
    if (Object.keys(data).filter(k => k === 'selected_quote').length > 0) {
      data = data as OpenQuoteRequestsItem;
      if (!data?.selected_quote?.duration && !data?.selected_quote?.duration_units) {
        return '';
      }
      return (data?.selected_quote?.duration ?? 1) + '-' + (data?.selected_quote?.duration_units ?? 'Year') + ' Term';
    } else {
      data = data as Quote;
      if (!data?.duration && !data?.duration_units) {
        return '';
      }
      return data?.duration + '-' + data?.duration_units + ' Term';
    }
  }

  getDateRangeString(): string {
    const effectiveDate = this.item?.data?.selected_quote?.bond_effective_date ?? null;
    const expirationDate = this.item?.data?.selected_quote?.bond_expiration_date ?? null;

    if (!effectiveDate || !expirationDate) {
      return '';
    }

    const datePipe = new DatePipe('en-US');
    const formattedEffectiveDate = datePipe.transform(effectiveDate, 'shortDate');
    const formattedExpirationDate = datePipe.transform(expirationDate, 'shortDate');

    return `${formattedEffectiveDate} - ${formattedExpirationDate}`;
  }


  getFormattedAddress(address: Address): string {
    return `${address?.street_1}${address?.street_2 ? ', ' + address?.street_2 : ''}, ${address?.city}, ${address?.state_abbreviation} ${address?.zip}`;
  }

  private createCartMenuItems(): CartMenuItem[] {
    return [
      // {
      //   label: 'View Details',
      //   // icon: 'heroicons_outline:eye',
      //   enabled: true,
      //   action: () => this.viewDetails.emit(this.item),
      // },
      {
        label: 'Remove',
        // icon: 'heroicons_outline:trash',
        enabled: true,
        action: () => this.removeItem.emit(this.item),
      },
      {
        label: 'Send Message',
        enabled: true,
        action: () => this.sendMessageFromDrawer.emit({ item: this.item, messageType: 'send-message' }),
      },
      {
        label: 'I need to make a change',
        enabled: this.item.data.quote_status_stage?.toLowerCase() === 'selling' || this.item.data.quote_status_stage?.toLowerCase() === 'quoting',
        action: () => this.sendMessageFromDrawer.emit({ item: this.item, messageType: 'make-a-change' }),
      },
      {
        label: 'Decline Quote',
        enabled: this.item.data.quote_status_stage?.toLowerCase() === 'selling',
        action: () => this.declineQuote.emit(this.item),
      }
    ];
  }

  // Quote Request specific methods
  getOrderNumber(): string {
    return this.item.data?.order_number || this.item.display_name;
  }

  getQuoteRequestDate(): Date | string | null {
    return this.item.data?.quote_request_date || null;
  }

  getCompanyType(): string {
    return this.item.data?.quote_request_bond?.company_type_label || '';
  }

  getBondType(): string {
    return this.item.data?.quote_request_bond?.surety_bond_label || '';
  }

  getBondAmount(): number {
    return this.item.data?.quote_request_bond?.bond_amount || 0;
  }

  getShippingOption(): string {
    return this.item.data?.shipping_details?.shipping_option_label || 'Standard delivery';
  }

  getAvailableShippingOptions(): Shipping[] {
    return this.item.data?.shipping_options || [];
  }

  getUserSelectableShippingOptions(): Shipping[] {
    const options = this.item.data?.shipping_options || [];
    return options.filter(option => option.shipping_option_user_selectable);
  }

    getSelectableShippingOptions(): Shipping[] {
    const options = this.item.data?.shipping_options || [];

    // Always include user-selectable options
    const userSelectableOptions = options.filter(option => option.shipping_option_user_selectable);

    // Always include non-user-selectable options so users can switch back to them
    // This allows switching back to Email even after selecting something else
    const nonUserSelectableOptions = options.filter(option => !option.shipping_option_user_selectable);

    // Combine them: non-user-selectable first (like Email), then user-selectable
    return [...nonUserSelectableOptions, ...userSelectableOptions];
  }

  getCurrentShippingOption(): Shipping | null {
    if (!this.item.data?.shipping_options || !this.item.data?.shipping_details?.shipping_option_id) {
      return null;
    }
    return this.item.data.shipping_options.find(
      option => option.shipping_option_id === +this.item.data!.shipping_details!.shipping_option_id!
    ) || null;
  }

  isCurrentOptionUserSelectable(): boolean {
    const currentOption = this.getCurrentShippingOption();
    return currentOption?.shipping_option_user_selectable || false;
  }

    shouldShowShippingDropdown(): boolean {
    const selectableOptions = this.getSelectableShippingOptions();
    // Show dropdown when there are multiple selectable shipping options available
    return selectableOptions.length > 1;
  }

  getCurrentShippingOptionId(): number | null {
    return this.item.data?.shipping_details?.shipping_option_id ? +this.item.data.shipping_details.shipping_option_id : null;
  }

  onShippingOptionChange(selectedShippingOptionId: number): void {
    if (!this.item.data.shipping_options) return;

    const selectedOption = this.item.data.shipping_options.find(
      option => option.shipping_option_id === selectedShippingOptionId
    );

    if (selectedOption) {
      const quoteRequestId = +this.item.data.quote_request_id;
      const quoteId = this.item.data.quote_request_bond?.selected_quote_id || this.item.data.selected_quote?.quote_id;

      if (!quoteId) {
        this._cathyService.error('Unable to update shipping option: No quote selected.');
        return;
      }

      // Call the backend service to update the shipping option
      this._openQuoteRequestsWidgetService.setShippingOption(quoteRequestId, quoteId, selectedShippingOptionId).pipe(
        tap(response => {
          if (response && response.success) {
            // Update the cart item's shipping data
            this.item.data.shipping_option_id = selectedShippingOptionId.toString();
            this.item.data.shipping_option_label = selectedOption.shipping_option_label;
            this.item.data.shipping_option_price = selectedOption.shipping_option_price;
            this.item.data.shipping_details = selectedOption;

            // Update the cart item's shipping cost for price calculations
            this.item.shipping = selectedOption.shipping_option_price;

            // Emit events to notify parents of the change
            this.updatedOpenQR.emit(this.item.data);
            this.itemUpdated.emit(this.item);
          } else {
            this._cathyService.error(response?.message || 'Failed to update shipping option.');
          }
        }),
        catchError(error => {
          this._cathyService.error('An error occurred while updating the shipping option.');
          console.error('Error updating shipping option:', error);
          return of(null); // Return an observable to continue the stream
        }),
        takeUntil(this._unsubscribeAll)
      ).subscribe();
    }
  }

  hasAttachedFiles(): boolean {
    return (this.item.data?.quote_request_bond?.related_files?.length ?? 0) > 0;
  }

  getAttachedFiles(): any[] {
    return this.item.data?.quote_request_bond?.related_files || [];
  }

  increaseQuantity() {
    this.updateQuantity.emit({ item: this.item, quantity: this.item.quantity + 1 });
  }

  decreaseQuantity() {
    if (this.item.quantity > 1) {
      this.updateQuantity.emit({ item: this.item, quantity: this.item.quantity - 1 });
    } else {
      this.removeItem.emit(this.item);
    }
  }

  protected readonly isNaN = isNaN;

  //If there is any change on the quote option I am emitting to the parent the update object
  setSelectedQuoteOption() {
    if (!this.item.data.quote_selection_form) {
      return;
    }

    const selectedQuoteValue = this.item.data.quote_selection_form.get('selected_quote_option')?.value;
    if (selectedQuoteValue !== undefined && this.item.data.quote_request_bond?.quotes) {
      const selectedQuote = this.item.data.quote_request_bond.quotes.findIndex((q, i) => q.premium === selectedQuoteValue);
      if (selectedQuote !== -1) {
        // Update the selected quote in the data
        this.item.data.selected_quote = this.item.data.quote_request_bond.quotes[selectedQuote];
        const quoteId = this.item.data.selected_quote.quote_id;
        const quoteRequestId = this.item.data.quote_request_id;

        // Call the backend service to update the selected quote
        this._openQuoteRequestsWidgetService.setSelectedQuoteOption(+quoteRequestId, quoteId)
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe({
            next: (updatedQuoteRequest) => {
              // Update the quote_request_bond.selected_quote_id
              this.item.data.quote_request_bond.selected_quote_id = quoteId;

              // Update the cart item's static price property for display consistency
              this.item.price = this.item.data.selected_quote.premium || 0;

              // Emit events to notify parents of the change
              this.updatedOpenQR.emit(this.item.data);
              this.itemUpdated.emit(this.item);
            },
            error: (error) => {
              console.error('Error updating selected quote option:', error);
              // Revert the UI change if the backend call fails
              // You might want to show an error message to the user here
            }
          });
      }
    }
  }

  getFileIcon(extension: string): string {
    const iconMap = {
      'pdf': 'picture_as_pdf',
      'doc': 'description',
      'docx': 'description',
      'jpg': 'image',
      'png': 'image',
    };

    return iconMap[extension] || 'insert_drive_file';
  }

  // ----- Communications helpers -----
  activeCommunicationContacts(): Array<ActiveCommunicationContact> {
    const cu = this._userService?.currentAccountUser() as { user_id?: number | string; id?: string; email?: string } | null;
    const data = this.item?.data;
    return activeCommunicationContactsFrom(data?.communication, {
      customerUserId: Number(data?.user_id),
      accountUserId: Number(cu?.user_id ?? cu?.id) || null,
      accountUserEmail: cu?.email,
      isRenewal: Number(data?.renewal_quote_request_id) > 0,
    });
  }

  isMissingEmail(email: string): boolean {
    if (!email) return true;
    const lowerEmail = email.toLowerCase();
    return lowerEmail.includes('service+missing') || lowerEmail.includes('service+max');
  }

  // Excludes only the current account user; contacts with placeholder (service+)
  // emails stay listed so the account user can still copy the purchase URL for them
  private validReminderContacts(): ActiveCommunicationContact[] {
    const currentUserEmail = this._userService?.currentAccountUser()?.email;
    return this.activeCommunicationContacts()
      .filter(c => c.email !== currentUserEmail);
  }

  //All reminder recipients
  reminderRecipients(): ActiveCommunicationContact[] {
    return this.validReminderContacts();
  }

  private getReminderSubjectAndBody(greetingName: string, rh?: string | null): { subject: string, body: string } {
    const isRenewal = (this.item?.data as any)?.renewal_quote_request_id > 0;
    const bondAmount = this.item?.data?.quote_request_bond?.bond_amount
      ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(this.item.data.quote_request_bond.bond_amount)
      : '';
    const suretyBondLabel = `${bondAmount} ${this.item?.data?.quote_request_bond?.state_abbreviation || ''} ${this.item?.data?.quote_request_bond?.surety_bond_label || ''}`.trim();
    const orderNumber = this.item?.data?.order_number || '';
    const dueDateStr = this.item?.data?.selected_quote?.bond_effective_date ? new Date(this.item.data.selected_quote.bond_effective_date).toLocaleDateString() : '';
    const jumpUrl = jumpUrlWithRh(this.item?.data?.selected_quote?.jump_url || '', rh);

    const subject = `Reminder to ${isRenewal ? 'renew' : 'purchase'} your ${suretyBondLabel} #${orderNumber}`;
    let body = '';

    if (isRenewal) {
      body = `${greetingName},\nPlease use the link below to go to Surety Bonds Direct to pay for your ${suretyBondLabel} renewal effective ${dueDateStr}.\n${jumpUrl}`;
    } else {
      body = `${greetingName},\nPlease use the link below to go to Surety Bonds Direct to purchase your ${suretyBondLabel}.\n${jumpUrl}`;
    }

    return { subject, body };
  }

  getReminderMailto(c: ActiveCommunicationContact): string {
    if (!c.user_id || !c.email || this.isMissingEmail(c.email)) return '';
    const greetingName = (c?.name || 'Customer').split(' ')[0];
    const { subject, body } = this.getReminderSubjectAndBody(greetingName, c.rh);
    return `mailto:${c.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  openSendReminderDialog(contact: ActiveCommunicationContact) {
    const mailto = this.getReminderMailto(contact);
    const jumpUrl = jumpUrlWithRh(this.item?.data?.selected_quote?.jump_url || '', contact.rh);

    this._dialog.open(SendReminderDialogComponent, {
      data: { mailto, jumpUrl, contactName: contact.name },
      width: '360px',
      autoFocus: false,
    });
  }

  isOverdue(): boolean {
    if (!this.item?.data.selected_quote?.bond_effective_date) return false;
    const dueDate = new Date(this.item.data.selected_quote.bond_effective_date);
    const tenDaysBefore = new Date(dueDate.getTime());
    tenDaysBefore.setDate(dueDate.getDate() - 10);
    return tenDaysBefore.getTime() > Date.now();
  }

  protected readonly isServiceEmail = isServiceEmail;
}
